import { type FC, type ReactNode } from "react";

import { Status } from "@snapchat/snap-design-system";
import { Field, useFormikContext } from "formik";
import { type MessageDescriptor, useIntl } from "react-intl";

import { FormikInput } from "../../../shared/formik";
import { NAME_MAX_CHARS } from "../../model";
import {
    NAME_ERROR_REQUIRED,
    NAME_ERROR_TOO_LONG,
    NAME_ERROR_UNSUPPORTED_CHARACTERS,
} from "../../validation/webLensFormSchemas";
import styles from "../WebLensPage.module.css";
import { NameCounter, messages } from "../messages.generated";

// The schema emits error CODES; the copy lives here in i18n messages (forms.md: never show raw
// Yup text to users). Partial because Formik types errors.name as any string — an unknown code
// (a schema rule added without copy) falls back to the required message rather than raw Yup text.
const NAME_ERROR_MESSAGES: Partial<Record<string, MessageDescriptor>> = {
    [NAME_ERROR_REQUIRED]: messages.nameErrorRequired,
    [NAME_ERROR_TOO_LONG]: messages.nameErrorTooLong,
    [NAME_ERROR_UNSUPPORTED_CHARACTERS]: messages.nameErrorUnsupportedCharacters,
};

interface WebLensNameFieldProps {
    disabled: boolean;
    hint?: ReactNode;
}

/**
 * The Formik-bound lens-name field shared by the submit and update pages: input, live counter and
 * schema-error display (after touch, per fieldErrorCreator's convention). Expects to render inside
 * a Formik context whose values carry a `name: string`.
 */
export const WebLensNameField: FC<WebLensNameFieldProps> = ({ disabled, hint }) => {
    const intl = useIntl();
    const { errors, touched, values } = useFormikContext<{ name: string }>();

    const errorCode = touched.name && errors.name ? errors.name : undefined;
    const error = errorCode
        ? intl.formatMessage(NAME_ERROR_MESSAGES[errorCode] ?? messages.nameErrorRequired, { max: NAME_MAX_CHARS })
        : undefined;

    return (
        <div>
            <Field
                name="name"
                component={FormikInput}
                label={intl.formatMessage(messages.nameLabel)}
                maxLength={NAME_MAX_CHARS}
                hideMaxLengthRemainder
                disabled={disabled}
                status={error}
                statusType={error && Status.ERROR}
            />
            <div className={styles.nameCounter}>
                <NameCounter count={values.name.length} max={NAME_MAX_CHARS} />
            </div>
            {hint ? <div className={styles.fieldHint}>{hint}</div> : null}
        </div>
    );
};
